import React from 'react';
import { Link } from 'react-router-dom';
import MetaTags from '../meta/MetaTags.jsx';
import './LandingSolutions.css';

const corporateBlocks = [
  { icon: '🏢', title: 'Centralized event hub', body: 'Manage attendee lists, meeting agendas, venue info, and timelines in one secure workspace instead of juggling multiple tools.' },
  { icon: '🗳️', title: 'Polls for faster decisions', body: 'Gather availability, session interest, and menu preferences with quick polls—perfect for offsites, workshops, and all-hands.' },
  { icon: '✅', title: 'Task delegation', body: 'Assign logistics to teammates—AV, catering, travel, printing badges—and track progress transparently.' },
  { icon: '💸', title: 'Budget oversight', body: 'Track budgets, deposits, and reimbursements in one view so finance and organizers stay aligned.' },
  { icon: '⭐', title: 'VIP and access levels', body: 'Offer executive or client-only sections with private details while keeping general attendees informed.' },
  { icon: '🔔', title: 'Instant updates', body: 'Change a venue or timing once; everyone sees it instantly with notifications tied to the same invite.' },
];

export default function CorporateEventSoftware() {
  return (
    <>
      <MetaTags profileKey="corporateEventSoftware" />
      <div className="landing-shell">
        <section className="section-hero">
          <span className="section-hero__eyebrow">Corporate Planning</span>
          <h1 className="section-hero__title">Corporate Event Planning Software</h1>
          <p className="section-hero__subtitle">
            Run meetings, conferences, and team offsites with digital invites, RSVP tracking, polls, tasks, and budgets—plus VIP access for executives and clients.
          </p>
          <div className="cta-row" aria-label="Planner call to action">
            <Link to="/planner" className="btn primary">Plan with PartyDecider</Link>
            <span className="cta-note">Built for modern teams; no downloads needed.</span>
          </div>
        </section>

        <section className="block-panel">
          <h2 className="block-panel__title">Everything in one secure planner</h2>
          <p className="block-panel__text">
            Coordinate agendas, travel, RSVPs, and budgets in a single platform. PartyDecider gives marketing, operations, and finance a shared source of truth for every corporate event.
          </p>
          <ul className="block-list" aria-label="Corporate planning features">
            {corporateBlocks.map((item) => (
              <li key={item.title} className="block-list__item">
                <span className="block-list__icon" aria-hidden="true">{item.icon}</span>
                <div>
                  <p className="block-list__title">{item.title}</p>
                  <p className="block-list__body">{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="section-columns">
          <div className="block-card">
            <div className="block-card__eyebrow">Meetings &amp; Workshops</div>
            <h3 className="block-card__title">Schedule smarter</h3>
            <p className="block-card__text">
              Propose times, collect votes, and publish confirmed agendas. Keep presenters and attendees aligned with a single shared timeline.
            </p>
          </div>
          <div className="block-card">
            <div className="block-card__eyebrow">Conferences &amp; Offsites</div>
            <h3 className="block-card__title">Handle vendors with clarity</h3>
            <p className="block-card__text">
              Track venues, AV, catering, and transportation tasks across teams. Assign owners and deadlines so nothing is missed.
            </p>
          </div>
          <div className="block-card">
            <div className="block-card__eyebrow">Security &amp; Control</div>
            <h3 className="block-card__title">Trusted by design</h3>
            <p className="block-card__text">
              Encrypted by default with VIP-only sections for sensitive sessions. Keep executive briefings private while sharing general details broadly.
            </p>
          </div>
        </section>

        <section className="block-panel">
          <h2 className="block-panel__title">Deploy a modern event workflow</h2>
          <p className="block-panel__text">
            Replace spreadsheets and fragmented email threads with a planner that unites invites, RSVPs, polls, tasks, and budgets. Your team gains speed, clarity, and fewer surprises on event day.
          </p>
          <div className="cta-row" aria-label="Planner call to action">
            <Link to="/planner" className="btn primary">Launch the Planner</Link>
            <Link to="/event-trip-planning" className="btn outline">See trip planning tools</Link>
          </div>
        </section>

        <section className="section-related" aria-label="Related internal resources">
          <div className="block-related">
            <p className="block-related__title">Trip &amp; Event Planning Tools</p>
            <p className="block-related__text">
              Explore collaborative itineraries, polls, tasks, budgets, and VIP access for group travel and events.
            </p>
            <Link to="/event-trip-planning" className="link-quiet">View trip &amp; event tools</Link>
          </div>
          <div className="block-related">
            <p className="block-related__title">Invitation &amp; Wedding Planning</p>
            <p className="block-related__text">
              Create paperless invitations, track RSVPs, and run your event timeline from a single invitation hub.
            </p>
            <Link to="/invitation-wedding-tools" className="link-quiet">View invitation services</Link>
          </div>
          <div className="block-related">
            <p className="block-related__title">Planner</p>
            <p className="block-related__text">
              Jump straight into the PartyDecider Planner to organize your next corporate event or retreat.
            </p>
            <Link to="/planner" className="link-quiet">Open Planner</Link>
          </div>
        </section>
      </div>
    </>
  );
}
